/**
 * Modulo Modules Injection
 * @module abstracts/core/modules
 * @namespace Abstracts/Core
 */

import loadScript from '../libs/load-scripts'
import resolveDomain from '../libs/resolve-domain';

/**
 * Classe injetora e gerenciadora de modulos da variável global window.GPAbstractModules no object Core
 *
 * @name Modules
 * @typedef {Object} Modules
 * @memberof abstracts/core/modules
 */
class Modules {
    /**
     * @property {Core}  Core - Classe Core
     * @private
     */
    _Core

    /**
     * @property {Array}  _modulesLoaded - Modulos carregados
     * @private
     */
    _modulesLoaded

    /**
     * @property {Boolean}  _loadScript - Indica se o scripts de modulos já foi carregado
     * @private
     */
    _loadedScript

    /**
     * Constructor da classe Modules
     *
     * @return {void}
     */
    constructor (Core) {
        this._Core = Core

        this._modulesLoaded = []

        this._loadedScript = false
    }

    /**
     * Carrega um novo modulo existente
     *
     * @return {void}
     * @param {string} module - Nome do modulo Status/Page
     */
    load (module) {
        return this._instance(module)
    }

    /**
     * Injeta um objeto do tema na classe Core
     *
     * @return {void}
     * @param {Object|Function} Module - Objeto ou Classe para ser injeto no Modulo Core
     */
    set (Module) {
        try {
            const Instance = (typeof Module === 'function') ? new Module() : Module

            if (typeof Instance.name === 'undefined') {
                throw new Error('O módulo deve ter uma um atributo name com o nome do Módulo')
            }

            if (!this._modulesLoaded.indexOf(Instance.name) !== -1) {
                this._Core[Instance.name] = Instance
                this._modulesLoaded.push(Instance.name)
            }

            return this.get(Instance.name)
        } catch (ex) {
            console.error(ex)
        }
    }

    /**
     * Retorna um modulo instanciado, seja um módulo do tema ou do Abstracts
     *
     * @return {void}
     * @param {Object|Function} Module - Objeto ou Classe para ser injeto no Modulo Core
     */
    get (Module) {
        // Verifica se é um modulo abstrato que ainda não foi carregado
        if (!this._modulesLoaded.indexOf(Module) !== -1 &&
            typeof this._getGlobal()[Module]) {
            this._instance(Module)
        }

        // Verifica se o modulo está carregado no Core
        if (this._modulesLoaded.indexOf(Module) !== -1) {
            return this._Core[Module]
        }
    }

    /**
     * Instância o objeto na classe Core
     *
     * @return {void}
     * @param {Object|Function} Module - Objeto ou Classe para ser injetado no Core
     */
    _instance (module) {
        try {
            // Verifica se o Modulo já não foi carregado
            if (this._modulesLoaded.indexOf(module) !== -1) {
                return this._Core[module]
            }

            // Verifica se o Module existe salvo na variável global
            const ModulesGlobal = this._getGlobal()
            if (typeof ModulesGlobal[module] === 'undefined') {
                throw new Error(`Modulo global ${module} não encontrado`)
            }

            // Instância o Modulo dentro do Core
            this._Core[module] = new ModulesGlobal[module]()

            // Adiciona a lista de modulos carregados
            this._modulesLoaded.push(module)

            // Deleta o Modulo da variável global
            delete window.GPAbstractModules[module]

            // Retorna o modulo para ser utulizado
            return this._Core[module]
        } catch (ex) {
            console.error(ex)
            return {}
        }
    }

    /**
     * Retorna o objeto global com a lista de modulos
     *
     * @return {void}
     * @param {Object|Function} Module - Objeto ou Classe para ser injetado no Core
     */
    _getGlobal () {
        return window.GPAbstractModules || {}
    }

    /**
     * Carrega o scripts de modulos
     *
     * @return {void}
     * @param {Object|Function} Module - Objeto ou Classe para ser injetado no Core
     */
    async loadScript () {
        if (this._loadScript) {
            return
        }

        const domain = resolveDomain()

        // Carrega o script requisitado
        await loadScript(`${domain}-abstracts-modules`)
        this._loadedScript = true
        return true
    }
}

export default Modules
